import { AnalyticalFilters } from '../types/filters';

/**
 * Build SQL WHERE clauses for analytical filters
 *
 * These filters are additive (AND logic) when multiple are active.
 * They filter school distribution data based on statistical criteria.
 *
 * @param filters - Active analytical filters
 * @param options - SQL context options
 * @returns SQL WHERE clause fragment (or empty string if no filters active)
 */
export function buildAnalyticalWhere(
  filters: AnalyticalFilters | undefined,
  options: {
    /** Alias for school value column */
    valueColumn?: string;
    /** Alias for SALSA expected value column */
    salsaColumn?: string;
    /** Alias for previous year value column */
    prevYearColumn?: string;
  } = {}
): string {
  if (!filters || Object.keys(filters).length === 0) {
    return '';
  }

  const {
    valueColumn = 's.value',
    salsaColumn = 'salsa.salsa_expected',
    prevYearColumn = 'prev.prev_value'
  } = options;

  const clauses: string[] = [];

  // Filter 1: Under expected (SALSA)
  // Only show schools where actual value < SALSA expected value
  if (filters.underExpected) {
    clauses.push(`${valueColumn} < ${salsaColumn}`);
  }

  // Filter 2: Over expected (SALSA)
  // Only show schools where actual value > SALSA expected value
  if (filters.overExpected) {
    clauses.push(`${valueColumn} > ${salsaColumn}`);
  }

  // Filter 3: Outliers (IQR-based)
  // Show only schools outside Q1 - 1.5*IQR or Q3 + 1.5*IQR
  if (filters.outliers) {
    clauses.push(`
      (${valueColumn} < iqr_bounds.q1 - 1.5 * iqr_bounds.iqr
       OR ${valueColumn} > iqr_bounds.q3 + 1.5 * iqr_bounds.iqr)
    `.trim());
  }

  // Filter 4: Top improvement (year-over-year delta)
  // Show only schools in top 25% of year-over-year improvement
  if (filters.topImprovement) {
    clauses.push(`
      ${prevYearColumn} IS NOT NULL
      AND (${valueColumn} - ${prevYearColumn}) >= improvement_threshold.p75_delta
    `.trim());
  }

  return clauses.length > 0 ? `AND (${clauses.join(' AND ')})` : '';
}

/**
 * Build CTEs (Common Table Expressions) needed for analytical filters
 *
 * These CTEs calculate statistical bounds (IQR, improvement thresholds, SALSA values)
 * that are referenced in the WHERE clauses.
 *
 * @param filters - Active analytical filters
 * @param params - Query parameters (kommunId, year, kpiId, governance)
 * @returns SQL CTE fragments
 */
export function buildAnalyticalCTEs(
  filters: AnalyticalFilters | undefined,
  params: {
    kommunId: string;
    year: number;
    kpiId: string;
    governance?: string;
  }
): string {
  if (!filters || Object.keys(filters).length === 0) {
    return '';
  }

  const ctes: string[] = [];

  // CTE for SALSA expected values (needed for underExpected/overExpected)
  if (filters.underExpected || filters.overExpected) {
    ctes.push(`
SalsaExpected AS (
  SELECT
    t.entity_id,
    t.value AS actual_value,
    CASE
      WHEN SUM(CASE WHEN t2.students > 0 THEN t2.students ELSE 0 END) > 0
        THEN t.value - (SUM(t2.value * t2.students) / NULLIF(SUM(t2.students), 0))
      ELSE t.value - AVG(t2.value)
    END AS salsa_expected
  FROM gold_kpi_trend t
  LEFT JOIN gold_kpi_trend t2 ON t2.municipality_id = t.municipality_id
    AND t2.kpi_id = 'U15416' -- SALSA avvikelse KPI
    AND t2.year = :year
    AND t2.entity_type = 'org_unit'
    AND t2.gender = 'T'
    ${params.governance ? "AND t2.governance_type_year = :governance" : ''}
  WHERE t.municipality_id = :kommunId
    AND t.kpi_id = :kpiId
    AND t.year = :year
    AND t.entity_type = 'org_unit'
    AND t.gender = 'T'
    ${params.governance ? "AND t.governance_type_year = :governance" : ''}
  GROUP BY t.entity_id, t.value
)
    `.trim());
  }

  // CTE for IQR bounds (needed for outliers filter)
  if (filters.outliers) {
    ctes.push(`
IQRBounds AS (
  SELECT
    PERCENTILE_CONT(0.25) WITHIN GROUP (ORDER BY value) AS q1,
    PERCENTILE_CONT(0.75) WITHIN GROUP (ORDER BY value) AS q3,
    PERCENTILE_CONT(0.75) WITHIN GROUP (ORDER BY value) -
    PERCENTILE_CONT(0.25) WITHIN GROUP (ORDER BY value) AS iqr
  FROM (
    SELECT AVG(t.value) AS value
    FROM gold_kpi_trend t
    WHERE t.municipality_id = :kommunId
      AND t.kpi_id = :kpiId
      AND t.year = :year
      AND t.entity_type = 'org_unit'
      AND t.gender = 'T'
      ${params.governance ? "AND t.governance_type_year = :governance" : ''}
      AND t.value IS NOT NULL
    GROUP BY t.entity_id
  )
)
    `.trim());
  }

  // CTE for improvement threshold (needed for topImprovement filter)
  if (filters.topImprovement) {
    ctes.push(`
ImprovementThreshold AS (
  SELECT
    PERCENTILE_CONT(0.75) WITHIN GROUP (ORDER BY delta) AS p75_delta
  FROM (
    SELECT
      t.entity_id,
      AVG(t.value) - AVG(t_prev.value) AS delta
    FROM gold_kpi_trend t
    LEFT JOIN gold_kpi_trend t_prev ON t_prev.entity_id = t.entity_id
      AND t_prev.kpi_id = t.kpi_id
      AND t_prev.year = :year - 1
      AND t_prev.entity_type = 'org_unit'
      AND t_prev.gender = 'T'
      ${params.governance ? "AND t_prev.governance_type_year = :governance" : ''}
    WHERE t.municipality_id = :kommunId
      AND t.kpi_id = :kpiId
      AND t.year = :year
      AND t.entity_type = 'org_unit'
      AND t.gender = 'T'
      ${params.governance ? "AND t.governance_type_year = :governance" : ''}
      AND t.value IS NOT NULL
      AND t_prev.value IS NOT NULL
    GROUP BY t.entity_id
  )
  WHERE delta IS NOT NULL
)
    `.trim());
  }

  return ctes.length > 0 ? ctes.join(',\n') + ',' : '';
}

/**
 * Check if any analytical filters are active
 */
export function hasActiveAnalyticalFilters(filters: AnalyticalFilters | undefined): boolean {
  if (!filters) return false;
  return Object.values(filters).some(v => v === true);
}

/**
 * Get labels for active analytical filters (for display in chips)
 */
export function getAnalyticalFilterLabels(filters: AnalyticalFilters | undefined): Array<{ key: string; label: string }> {
  if (!filters) return [];

  const labels: Array<{ key: string; label: string }> = [];

  if (filters.underExpected) labels.push({ key: 'underExpected', label: 'Under förväntat' });
  if (filters.overExpected) labels.push({ key: 'overExpected', label: 'Över förväntat' });
  if (filters.outliers) labels.push({ key: 'outliers', label: 'Avvikare' });
  if (filters.topImprovement) labels.push({ key: 'topImprovement', label: 'Mest förbättrade' });

  return labels;
}
